import React, {
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';

import {
    Alert,
    Button,
    Col,
    ControlLabel,
    FormControl,
    FormGroup,
    Modal,
    Row
} from 'react-bootstrap';

import {ApiContext} from '../../../api/Api';


const today = () => {
    const now = new Date();

    return [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, '0'),
        String(now.getDate()).padStart(2, '0')
    ].join('-');
};


const collection = response => {
    if (!response)
        return [];

    if (Array.isArray(response))
        return response;

    if (Array.isArray(response.data))
        return response.data;

    if (Array.isArray(response['hydra:member']))
        return response['hydra:member'];

    if (
        response.data &&
        Array.isArray(response.data['hydra:member'])
    )
        return response.data['hydra:member'];

    return [];
};


const data = response =>
    response && response.data
        ? response.data
        : response || {};


const relationId = relation => {
    if (!relation)
        return '';

    if (typeof relation === 'object' && relation.id)
        return String(relation.id);

    const value =
        typeof relation === 'object'
            ? relation['@id']
            : relation;

    if (!value)
        return '';

    return String(value).split('/').filter(Boolean).pop() || '';
};


const guestLabel = visit => {
    const guest = visit.guest || {};

    const name =
        visit.guestName ||
        guest.fullName ||
        guest.name ||
        'Huésped';

    const folio =
        visit.folio ||
        visit.folioNumber ||
        '';

    const lounge =
        visit.originLounge &&
        visit.originLounge.name
            ? visit.originLounge.name
            : '';

    return [
        name,
        folio ? `Folio ${folio}` : '',
        lounge
    ].filter(Boolean).join(' · ');
};


const staffLabel = staff => {
    const employee = staff.employee || {};

    if (typeof employee === 'string')
        return `Colaborador ${relationId(employee)}`;

    return (
        employee.fullName ||
        [
            employee.name,
            employee.patLastName,
            employee.matLastName
        ].filter(Boolean).join(' ') ||
        `Colaborador ${relationId(employee)}`
    );
};


const loungeLabel = (resource, lounges=[]) => {
    const lounge = resource && resource.lounge;

    if (!lounge)
        return '';

    if (typeof lounge === 'object' && lounge.name)
        return lounge.name;

    const loungeId = relationId(lounge);

    const matchedLounge = lounges.find(
        item => String(item.id) === String(loungeId)
    );

    if (matchedLounge && matchedLounge.name)
        return matchedLounge.name;

    return loungeId ? `Sala ${loungeId}` : '';
};


const slotValue = slot => {
    if (!slot)
        return '';

    if (typeof slot === 'string')
        return slot;

    return (
        slot.start ||
        slot.scheduledStartDate ||
        slot.dateTime ||
        slot.datetime ||
        slot.value ||
        ''
    );
};


const slotLabel = slot => {
    const value = slotValue(slot);

    if (/^\d{2}:\d{2}/.test(value))
        return value.substring(0, 5);

    const match = String(value).match(/T(\d{2}:\d{2})/);

    return match ? match[1] : value;
};


const appointmentDateTime = (slot, date) => {
    const value = slotValue(slot);

    if (/^\d{2}:\d{2}$/.test(value))
        return `${date}T${value}:00`;

    if (/^\d{2}:\d{2}:\d{2}$/.test(value))
        return `${date}T${value}`;

    return value;
};


const NewSpaAppointmentModal = ({
    show,
    onHide,
    onCreated
}) => {

    const api = useContext(ApiContext);

    const [visits, setVisits] = useState([]);
    const [services, setServices] = useState([]);
    const [resources, setResources] = useState([]);
    const [staffs, setStaffs] = useState([]);
    const [lounges, setLounges] = useState([]);

    const [visitId, setVisitId] = useState('');
    const [serviceId, setServiceId] = useState('');
    const [resourceId, setResourceId] = useState('');
    const [staffId, setStaffId] = useState('');
    const [date, setDate] = useState(today());
    const [slots, setSlots] = useState([]);
    const [selectedSlot, setSelectedSlot] = useState('');
    const [comments, setComments] = useState('');

    const [loading, setLoading] = useState(false);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');


    const selectedService = useMemo(
        () => services.find(
            service => String(service.id) === String(serviceId)
        ),
        [services, serviceId]
    );


    const selectedResource = useMemo(
        () => resources.find(
            resource => String(resource.id) === String(resourceId)
        ),
        [resources, resourceId]
    );


    const requiresStaff = !!(
        selectedService &&
        (
            selectedService.requiresStaff === true ||
            selectedService.requiresStaff === 1 ||
            selectedService.requiresStaff === '1'
        )
    );


    const serviceResourceType = relationId(
        selectedService && selectedService.resourceType
    );


    const compatibleResources = useMemo(
        () => resources.filter(resource => {
            const active =
                resource.active === true ||
                resource.active === 1 ||
                resource.active === '1';

            if (!active)
                return false;

            if (
                resource.status &&
                resource.status !== 'AVAILABLE'
            )
                return false;

            if (!serviceResourceType)
                return true;

            return (
                relationId(resource.resourceType) ===
                serviceResourceType
            );
        }),
        [resources, serviceResourceType]
    );


    const activeStaffs = useMemo(
        () => staffs.filter(staff => (
            staff.active === true ||
            staff.active === 1 ||
            staff.active === '1'
        )),
        [staffs]
    );


    useEffect(() => {
        if (!show)
            return;

        setLoading(true);
        setError('');

        Promise.all([
            api.spaEligibleVisits.get({
                customProp: '_spaEligibleVisits'
            }),
            api.spaServices.get({
                filters: {
                    pagination: false,
                    active: 1,
                    'order[name]': 'ASC'
                },
                customProp: '_spaServices'
            }),
            api.spaResources.get({
                filters: {
                    pagination: false,
                    active: 1,
                    status: 'AVAILABLE',
                    'order[name]': 'ASC'
                },
                customProp: '_spaResources'
            }),
            api.spaStaffs.get({
                filters: {
                    pagination: false,
                    active: 1
                },
                customProp: '_spaStaffs'
            }),
            api.lounges.get2({
                filters: {
                    isLounge: true
                },
                customProp: '_spaLounges'
            })
        ])
            .then(responses => {
                setVisits(collection(responses[0]));
                setServices(collection(responses[1]));
                setResources(collection(responses[2]));
                setStaffs(collection(responses[3]));
                setLounges(collection(responses[4]));
            })
            .catch(() =>
                setError(
                    'No fue posible cargar los datos para crear la cita.'
                )
            )
            .finally(() =>
                setLoading(false)
            );

    }, [show, api]);


    useEffect(() => {
        setResourceId('');
        setStaffId('');
        setSelectedSlot('');
        setSlots([]);
    }, [serviceId]);


    useEffect(() => {
        setSelectedSlot('');
        setSlots([]);

        if (!selectedService || !selectedResource || !date)
            return;

        if (requiresStaff && !staffId)
            return;

        const loungeId = relationId(selectedResource.lounge);

        if (!loungeId)
            return;

        const filters = {
            service: selectedService.id,
            lounge: loungeId,
            resource: selectedResource.id,
            date
        };

        if (requiresStaff)
            filters.staff = staffId;

        setLoadingSlots(true);
        setError('');

        api.spaAvailability.get({
            filters,
            customProp: '_spaAvailability'
        })
            .then(response => {
                const result = data(response);

                setSlots(
                    Array.isArray(result.slots)
                        ? result.slots.filter(
                            slot => slot.available === true
                        )
                        : []
                );
            })
            .catch(() =>
                setError(
                    'No fue posible consultar la disponibilidad.'
                )
            )
            .finally(() =>
                setLoadingSlots(false)
            );

    }, [
        api,
        selectedService,
        selectedResource,
        staffId,
        date,
        requiresStaff
    ]);


    const canCreate =
        !!visitId &&
        !!selectedService &&
        !!selectedResource &&
        !!selectedSlot &&
        (!requiresStaff || !!staffId) &&
        !saving;


    const createAppointment = () => {
        if (!canCreate)
            return;

        const loungeId = relationId(selectedResource.lounge);

        const scheduledStartDate =
            appointmentDateTime(selectedSlot, date);

        const appointment = {
            visitId,
            serviceId: selectedService.id,
            loungeId,
            resourceId: selectedResource.id,
            scheduledStartDate
        };

        if (requiresStaff)
            appointment.staffId = staffId;

        if (comments.trim())
            appointment.comments = comments.trim();

        setSaving(true);
        setError('');

        api.spaAppointments.create({
            appointment,
            customProp: '_spaAppointmentCreate'
        })
            .then(() => {
                onCreated();
                onHide();
            })
            .catch(() =>
                setError(
                    'No fue posible crear la cita. El horario pudo haber cambiado.'
                )
            )
            .finally(() =>
                setSaving(false)
            );
    };


    return (
        <Modal
            show={show}
            onHide={saving ? undefined : onHide}
            bsSize="large"
        >
            <Modal.Header closeButton={!saving}>
                <Modal.Title>Nueva cita SPA</Modal.Title>
            </Modal.Header>

            <Modal.Body>

                {error &&
                    <Alert bsStyle="danger">
                        {error}
                    </Alert>
                }

                {loading &&
                    <Alert bsStyle="info">
                        Cargando catálogos SPA...
                    </Alert>
                }

                <Row>
                    <Col md={12}>
                        <FormGroup>
                            <ControlLabel>Huésped</ControlLabel>

                            <FormControl
                                componentClass="select"
                                value={visitId}
                                onChange={e => setVisitId(e.target.value)}
                                disabled={loading}
                            >
                                <option value="">
                                    Selecciona un huésped...
                                </option>

                                {visits.map(visit =>
                                    <option
                                        key={visit.id || visit.visitId}
                                        value={visit.id || visit.visitId}
                                    >
                                        {guestLabel(visit)}
                                    </option>
                                )}
                            </FormControl>
                        </FormGroup>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <FormGroup>
                            <ControlLabel>Servicio</ControlLabel>

                            <FormControl
                                componentClass="select"
                                value={serviceId}
                                onChange={e => setServiceId(e.target.value)}
                                disabled={loading}
                            >
                                <option value="">
                                    Selecciona un servicio...
                                </option>

                                {services.map(service =>
                                    <option
                                        key={service.id}
                                        value={service.id}
                                    >
                                        {service.name}
                                        {' · '}
                                        {service.durationMinutes}
                                        {' min'}
                                    </option>
                                )}
                            </FormControl>
                        </FormGroup>
                    </Col>

                    <Col md={6}>
                        <FormGroup>
                            <ControlLabel>Recurso</ControlLabel>

                            <FormControl
                                componentClass="select"
                                value={resourceId}
                                onChange={e => setResourceId(e.target.value)}
                                disabled={!selectedService}
                            >
                                <option value="">
                                    Selecciona un recurso...
                                </option>

                                {compatibleResources.map(resource =>
                                    <option
                                        key={resource.id}
                                        value={resource.id}
                                    >
                                        {resource.name}
                                        {' · '}
                                        {loungeLabel(resource, lounges)}
                                    </option>
                                )}
                            </FormControl>
                        </FormGroup>
                    </Col>
                </Row>

                {requiresStaff &&
                    <Row>
                        <Col md={12}>
                            <FormGroup>
                                <ControlLabel>Personal SPA</ControlLabel>

                                <FormControl
                                    componentClass="select"
                                    value={staffId}
                                    onChange={e => setStaffId(e.target.value)}
                                >
                                    <option value="">
                                        Selecciona personal...
                                    </option>

                                    {activeStaffs.map(staff =>
                                        <option
                                            key={staff.id}
                                            value={staff.id}
                                        >
                                            {staffLabel(staff)}
                                        </option>
                                    )}
                                </FormControl>
                            </FormGroup>

                            {activeStaffs.length === 0 &&
                                <Alert bsStyle="warning">
                                    Este servicio requiere personal SPA,
                                    pero no existe personal activo configurado.
                                </Alert>
                            }
                        </Col>
                    </Row>
                }

                <Row>
                    <Col md={6}>
                        <FormGroup>
                            <ControlLabel>Fecha</ControlLabel>

                            <FormControl
                                type="date"
                                min={today()}
                                value={date}
                                onChange={e => setDate(e.target.value)}
                            />
                        </FormGroup>
                    </Col>

                    <Col md={6}>
                        <FormGroup>
                            <ControlLabel>
                                Horario disponible
                            </ControlLabel>

                            <FormControl
                                componentClass="select"
                                value={slotValue(selectedSlot)}
                                onChange={e => {
                                    const found = slots.find(
                                        slot =>
                                            slotValue(slot) ===
                                            e.target.value
                                    );

                                    setSelectedSlot(
                                        found || e.target.value
                                    );
                                }}
                                disabled={
                                    !selectedResource ||
                                    loadingSlots ||
                                    (requiresStaff && !staffId)
                                }
                            >
                                <option value="">
                                    {loadingSlots
                                        ? 'Consultando...'
                                        : (
                                            slots.length === 0 &&
                                            selectedResource
                                                ? 'No hay horarios disponibles'
                                                : 'Selecciona un horario...'
                                        )
                                    }
                                </option>

                                {slots.map((slot, index) =>
                                    <option
                                        key={`${slotValue(slot)}-${index}`}
                                        value={slotValue(slot)}
                                    >
                                        {slotLabel(slot)}
                                    </option>
                                )}
                            </FormControl>
                        </FormGroup>
                    </Col>
                </Row>

                <Row>
                    <Col md={12}>
                        <FormGroup>
                            <ControlLabel>Comentarios</ControlLabel>

                            <FormControl
                                componentClass="textarea"
                                rows={3}
                                value={comments}
                                onChange={e => setComments(e.target.value)}
                                placeholder="Opcional"
                            />
                        </FormGroup>
                    </Col>
                </Row>

            </Modal.Body>

            <Modal.Footer>
                <Button
                    onClick={onHide}
                    disabled={saving}
                >
                    Cancelar
                </Button>

                <Button
                    bsStyle="primary"
                    onClick={createAppointment}
                    disabled={!canCreate}
                >
                    {saving ? 'Creando...' : 'Crear cita'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};


export default NewSpaAppointmentModal;
